import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  Sparkles, 
  Loader2,
  Copy,
  Download,
  ExternalLink,
  Clock,
  CheckCircle2,
  Users,
  ChevronDown,
  ChevronUp,
  Flame,
  Save,
  Wand2,
  Monitor,
  Smartphone,
  Target,
  Zap
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { dayContent } from "@/data/dayContent";
import AIResponseRenderer from "@/components/AIResponseRenderer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FOUNDER_PHOTO = "https://customer-assets.emergentagent.com/job_spirit-build-7/artifacts/oje4dzzl_full%20image.jpg";

// Prayer Overlay Component
const PrayerOverlay = ({ prayer, scripture, onComplete }) => {
  const [checked, setChecked] = useState(false);
  
  return (
    <div 
      className="fixed inset-0 bg-gradient-to-b from-[#1a1612] via-[#0f0d0a] to-[#0a0908] z-50 overflow-visible"
      style={{ minHeight: '100vh', overflow: 'auto' }}
    >
      <div className="dawn-glow" />
      <div 
        className="max-w-2xl w-full mx-auto text-center relative z-10 px-4"
        style={{ paddingTop: '40px', paddingBottom: '40px', display: 'block', position: 'static' }}
      >
        <p className="text-[#d4af37] text-lg mb-4 tracking-wide" style={{ display: 'block', position: 'static' }}>
          We don't do anything without honoring God around here.
        </p>
        
        {scripture && (
          <p className="text-zinc-500 text-sm mb-8 italic">
            "{scripture.text}" — {scripture.reference}
          </p>
        )}
        
        <Card className="bg-[#1a1814]/80 border-[#d4af37]/20 p-8 mb-8 backdrop-blur-sm">
          <h2 className="text-white font-medium mb-6">Opening Prayer</h2>
          <p className="text-zinc-300 leading-relaxed whitespace-pre-line italic">
            {prayer}
          </p>
        </Card>
        
        <div className="flex items-center justify-center gap-3 mb-6">
          <Checkbox 
            id="prayer-check"
            checked={checked}
            onCheckedChange={setChecked}
            className="border-[#d4af37] data-[state=checked]:bg-[#d4af37] data-[state=checked]:border-[#d4af37]"
          />
          <label htmlFor="prayer-check" className="text-zinc-300 cursor-pointer">
            I've prayed and I'm ready to begin
          </label>
        </div>
        
        <Button 
          onClick={onComplete}
          disabled={!checked}
          className={`rounded-full px-8 py-6 ${checked ? 'btn-gold gold-glow' : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'}`}
        >
          Begin Day
        </Button>
      </div>
    </div>
  );
};

// Progress Header with Gamification
const ProgressHeader = ({ currentDay, completedDays, streak }) => {
  const progressPercent = Math.round((completedDays / 7) * 100);
  
  return (
    <div className="mb-8">
      {/* Progress Dots + Streak */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4, 5, 6, 7].map(day => (
            <div 
              key={day}
              className={`w-3 h-3 rounded-full transition-all ${
                day < currentDay ? 'bg-[#d4af37]' : 
                day === currentDay ? 'bg-[#d4af37] ring-2 ring-[#d4af37]/30 ring-offset-2 ring-offset-[#0f0d0a]' : 
                'bg-zinc-700'
              }`}
            />
          ))}
          <span className="text-zinc-500 text-sm ml-2">Day {currentDay} of 7</span>
        </div>
        
        {streak > 0 && (
          <div className="flex items-center gap-1 text-[#d4af37]">
            <Flame className="w-4 h-4" />
            <span className="text-sm font-medium">{streak} day streak</span>
          </div>
        )}
      </div>
      
      {/* Completion Percentage */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#d4af37] to-[#FFD700] transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <span className="text-zinc-500 text-sm">{progressPercent}% complete</span>
      </div>
    </div>
  );
};

// Context Cards - Show data from previous days
const ContextCards = ({ type, data }) => {
  if (!data || Object.keys(data).length === 0) return null;
  
  const renderCard = () => {
    switch (type) {
      case 'top_pain_points':
        if (!data.top_pain_points?.length) return null;
        return (
          <Card className="bg-[#1a1814]/60 border-zinc-800 p-4">
            <h4 className="text-[#d4af37] text-xs uppercase tracking-widest mb-3">From Day 1: Top Pain Points</h4>
            <ul className="space-y-2">
              {data.top_pain_points.slice(0, 3).map((pain, i) => (
                <li key={i} className="text-zinc-300 text-sm flex items-start gap-2">
                  <span className="text-[#d4af37]">•</span>
                  {pain}
                </li>
              ))}
            </ul>
          </Card>
        );
      
      case 'top_strengths':
        if (!data.strengths_list?.length) return null;
        return (
          <Card className="bg-[#1a1814]/60 border-zinc-800 p-4">
            <h4 className="text-[#d4af37] text-xs uppercase tracking-widest mb-3">From Day 2: Top Strengths</h4>
            <ul className="space-y-2">
              {data.strengths_list.slice(0, 3).map((s, i) => (
                <li key={i} className="text-zinc-300 text-sm flex items-start gap-2">
                  <span className="text-[#d4af37]">•</span>
                  {s.strength}: {s.proof}
                </li>
              ))}
            </ul>
          </Card>
        );
      
      case 'selected_idea':
        if (!data.selected_idea) return null;
        return (
          <Card className="bg-[#1a1814]/60 border-[#d4af37]/20 p-4">
            <h4 className="text-[#d4af37] text-xs uppercase tracking-widest mb-2">Your Selected Idea</h4>
            <p className="text-white font-medium">{data.selected_idea}</p>
            {data.selected_idea_fit && (
              <p className="text-zinc-400 text-sm mt-2">{data.selected_idea_fit}</p>
            )}
          </Card>
        );
      
      case 'mvp_scope':
        if (!data.smallest_delivery) return null;
        return (
          <Card className="bg-[#1a1814]/60 border-zinc-800 p-4">
            <h4 className="text-[#d4af37] text-xs uppercase tracking-widest mb-2">Your MVP Scope</h4>
            <p className="text-zinc-300 text-sm">{data.smallest_delivery}</p>
          </Card>
        );
      
      case 'landing_page_copy':
        if (!data.landing_copy_saved) return null;
        return (
          <Card className="bg-[#1a1814]/60 border-[#d4af37]/20 p-4">
            <h4 className="text-[#d4af37] text-xs uppercase tracking-widest mb-3">Your Landing Page Copy (from Day 5)</h4>
            <div className="space-y-3 text-sm">
              {data.headline && <p className="text-white font-medium">{data.headline}</p>}
              {data.subheadline && <p className="text-zinc-400">{data.subheadline}</p>}
            </div>
          </Card>
        );
      
      case 'icp_summary':
        return (
          <Card className="bg-[#1a1814]/60 border-zinc-800 p-4">
            <h4 className="text-[#d4af37] text-xs uppercase tracking-widest mb-2">Your Ideal Customer</h4>
            <p className="text-zinc-300 text-sm">
              {data.icp_name || 'Someone'} who struggles with {data.top_pain_points?.[0] || 'the problem you identified'}
            </p>
          </Card>
        );
      
      default:
        return null;
    }
  };
  
  return renderCard();
};

// Example Cards Component for Day 1
const ExampleCards = ({ examples }) => {
  if (!examples?.length) return null;
  
  return (
    <div className="mb-8">
      <h3 className="text-[#d4af37] text-sm font-medium mb-4">Need inspiration? Here are real examples:</h3>
      <div className="grid md:grid-cols-3 gap-4">
        {examples.map((example, index) => (
          <Card key={index} className="bg-[#1a1814]/60 border-[#d4af37]/30 p-4">
            <p className="text-white font-medium text-sm mb-2">{example.painPoint}</p>
            <div className="space-y-1 text-xs">
              <p className="text-zinc-400"><span className="text-[#d4af37]">Who:</span> {example.who}</p>
              <p className="text-zinc-400"><span className="text-[#d4af37]">Existing Solutions:</span> {example.existingSolutions}</p>
              <p className="text-zinc-400"><span className="text-[#d4af37]">What's Missing:</span> {example.missing}</p>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Pain Point Matrix Component
const PainPointMatrix = ({ data, onChange, matrixPrompt, onSaveDraft, isSaving }) => {
  const [rows, setRows] = useState(data || Array(10).fill({ pain: '', who: '', solutions: '', missing: '' }));
  const filledCount = rows.filter(r => r.pain?.trim()).length;
  
  const updateRow = (index, field, value) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], [field]: value };
    setRows(newRows);
    onChange(newRows);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-medium">Pain Point Matrix</h3>
        <div className="flex items-center gap-2 bg-[#1a1814] px-3 py-1.5 rounded-full">
          <span className={`text-sm font-medium ${filledCount >= 10 ? 'text-[#d4af37]' : 'text-zinc-400'}`}>
            {filledCount}/10
          </span>
          {filledCount >= 10 && <CheckCircle2 className="w-4 h-4 text-[#d4af37]" />}
        </div>
      </div>
      
      {/* Matrix Prompt */}
      {matrixPrompt && (
        <p className="text-[#d4af37] text-sm italic mb-4">{matrixPrompt}</p>
      )}
      
      <div className="overflow-x-auto rounded-lg border border-zinc-800">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#1a1814]">
              <th className="text-left text-zinc-400 text-xs uppercase tracking-wider p-3 w-8">#</th>
              <th className="text-left text-zinc-400 text-xs uppercase tracking-wider p-3">Pain Point</th>
              <th className="text-left text-zinc-400 text-xs uppercase tracking-wider p-3">Who Faces This</th>
              <th className="text-left text-zinc-400 text-xs uppercase tracking-wider p-3">Existing Solutions</th>
              <th className="text-left text-zinc-400 text-xs uppercase tracking-wider p-3">What's Missing</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index} className="border-t border-zinc-800/50 hover:bg-[#1a1814]/30">
                <td className="p-2 text-zinc-500 text-center">{index + 1}</td>
                <td className="p-2">
                  <Input 
                    value={row.pain || ''}
                    onChange={(e) => updateRow(index, 'pain', e.target.value)}
                    className="bg-transparent border-none text-white text-sm focus:ring-1 focus:ring-[#d4af37]/50"
                    placeholder="Describe the problem..."
                  />
                </td>
                <td className="p-2">
                  <Input 
                    value={row.who || ''}
                    onChange={(e) => updateRow(index, 'who', e.target.value)}
                    className="bg-transparent border-none text-white text-sm focus:ring-1 focus:ring-[#d4af37]/50"
                    placeholder="Who has this?"
                  />
                </td>
                <td className="p-2">
                  <Input 
                    value={row.solutions || ''}
                    onChange={(e) => updateRow(index, 'solutions', e.target.value)}
                    className="bg-transparent border-none text-white text-sm focus:ring-1 focus:ring-[#d4af37]/50"
                    placeholder="What do they try?"
                  />
                </td>
                <td className="p-2">
                  <Input 
                    value={row.missing || ''}
                    onChange={(e) => updateRow(index, 'missing', e.target.value)}
                    className="bg-transparent border-none text-white text-sm focus:ring-1 focus:ring-[#d4af37]/50"
                    placeholder="What's broken?"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Progress bar */}
      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-[#d4af37] to-[#FFD700] transition-all"
          style={{ width: `${(filledCount / 10) * 100}%` }}
        />
      </div>
      
      {/* Save Draft Button */}
      <div className="flex flex-col items-start gap-2 mt-4">
        <Button 
          onClick={onSaveDraft}
          variant="outline"
          className="border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37]/10"
          disabled={isSaving}
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Draft
        </Button>
        <p className="text-zinc-500 text-xs">Your work saves automatically. Come back and add more throughout the day.</p>
      </div>
    </div>
  );
};

// Strength Examples Component for Day 2
const StrengthExamples = ({ examples }) => {
  if (!examples?.length) return null;
  
  return (
    <div className="mb-6">
      <h3 className="text-[#d4af37] text-sm font-medium mb-4">Need inspiration? Here are real examples:</h3>
      <div className="grid md:grid-cols-2 gap-3">
        {examples.map((example, index) => (
          <Card key={index} className="bg-[#1a1814]/60 border-[#d4af37]/30 p-4">
            <p className="text-zinc-300 text-sm italic">"{example}"</p>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Ask Three People Component for Day 2
const AskThreePeople = ({ config, data, onChange }) => {
  const [responses, setResponses] = useState(data || { person1: { name: '', response: '' }, person2: { name: '', response: '' }, person3: { name: '', response: '' } });
  const [copied, setCopied] = useState(false);
  
  const updateResponse = (person, field, value) => {
    const newResponses = { ...responses, [person]: { ...responses[person], [field]: value } };
    setResponses(newResponses);
    onChange(newResponses);
  };
  
  const copyMessage = () => {
    navigator.clipboard.writeText(config.message);
    setCopied(true);
    toast.success("Message copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card className="bg-[#1a1814]/60 border-zinc-800 p-6">
      <h3 className="text-white font-medium mb-2">{config.header}</h3>
      <p className="text-zinc-400 text-sm mb-4">{config.body}</p>
      
      {/* Copyable Message */}
      <div className="relative mb-6">
        <div className="bg-[#0f0d0a] border border-[#d4af37]/30 rounded-lg p-4">
          <p className="text-zinc-300 text-sm italic pr-10">"{config.message}"</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={copyMessage}
          className="absolute top-2 right-2 text-[#d4af37] hover:bg-[#d4af37]/10"
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
      
      {/* Response Fields */}
      <div className="space-y-4">
        {['person1', 'person2', 'person3'].map((person, index) => (
          <div key={person} className="grid md:grid-cols-3 gap-3 p-4 bg-[#0f0d0a]/50 rounded-lg border border-zinc-800/50">
            <div>
              <label className="block text-zinc-500 text-xs mb-1">Person {index + 1} Name</label>
              <Input
                value={responses[person]?.name || ''}
                onChange={(e) => updateResponse(person, 'name', e.target.value)}
                className="bg-[#0f0d0a] border-zinc-700 text-white text-sm"
                placeholder="Their name"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-zinc-500 text-xs mb-1">What they said</label>
              <Input
                value={responses[person]?.response || ''}
                onChange={(e) => updateResponse(person, 'response', e.target.value)}
                className="bg-[#0f0d0a] border-zinc-700 text-white text-sm"
                placeholder="Their answer about your strengths..."
              />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

// Strengths List Component
const StrengthsList = ({ data, onChange, strengthsPrompt, onSaveDraft, isSaving }) => {
  const [rows, setRows] = useState(data || Array(7).fill({ strength: '', proof: '' }));
  const filledCount = rows.filter(r => r.strength?.trim() && r.proof?.trim()).length;
  
  const updateRow = (index, field, value) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], [field]: value };
    setRows(newRows);
    onChange(newRows);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-white font-medium">My Strengths with Proof</h3>
        <span className="text-zinc-400 text-sm">{filledCount}/7 completed</span>
      </div>
      
      {/* Strengths Prompt */}
      {strengthsPrompt && (
        <p className="text-[#d4af37] text-sm italic mb-4">{strengthsPrompt}</p>
      )}
      
      <div className="space-y-3">
        {rows.map((row, index) => (
          <div key={index} className="grid md:grid-cols-2 gap-3 p-4 bg-[#1a1814]/50 rounded-lg border border-zinc-800/50">
            <div>
              <label className="block text-zinc-500 text-xs mb-1">Strength #{index + 1}</label>
              <Input 
                value={row.strength || ''}
                onChange={(e) => updateRow(index, 'strength', e.target.value)}
                className="bg-[#0f0d0a] border-zinc-700 text-white"
                placeholder="e.g., I'm organized"
              />
            </div>
            <div>
              <label className="block text-zinc-500 text-xs mb-1">Proof</label>
              <Input 
                value={row.proof || ''}
                onChange={(e) => updateRow(index, 'proof', e.target.value)}
                className="bg-[#0f0d0a] border-zinc-700 text-white"
                placeholder="e.g., Managed a team of 12, never missed a deadline"
              />
            </div>
          </div>
        ))}
      </div>
      
      {/* Save Draft Button */}
      <div className="flex flex-col items-start gap-2 mt-4">
        <Button 
          onClick={onSaveDraft}
          variant="outline"
          className="border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37]/10"
          disabled={isSaving}
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Draft
        </Button>
        <p className="text-zinc-500 text-xs">Come back and add more as things come to you today.</p>
      </div>
    </div>
  );
};

// Strength Bridge Table
const StrengthBridgeTable = ({ data, onChange, exampleRow }) => {
  const [rows, setRows] = useState(data || Array(5).fill({ strength: '', proof: '', who: '', ideas: '' }));
  
  const updateRow = (index, field, value) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], [field]: value };
    setRows(newRows);
    onChange(newRows);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-white font-medium">Strength-to-Business Bridge</h3>
      
      <div className="overflow-x-auto rounded-lg border border-zinc-800">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[#1a1814]">
              <th className="text-left text-zinc-400 text-xs uppercase tracking-wider p-3">My Strength</th>
              <th className="text-left text-zinc-400 text-xs uppercase tracking-wider p-3">Proof</th>
              <th className="text-left text-zinc-400 text-xs uppercase tracking-wider p-3">Who Benefits</th>
              <th className="text-left text-zinc-400 text-xs uppercase tracking-wider p-3">Business Ideas</th>
            </tr>
          </thead>
          <tbody>
            {/* Example Row (greyed out) */}
            {exampleRow && (
              <tr className="border-t border-zinc-800/50 bg-zinc-900/30 opacity-60">
                <td className="p-3 text-zinc-500 text-sm italic">{exampleRow.strength}</td>
                <td className="p-3 text-zinc-500 text-sm italic">{exampleRow.proof}</td>
                <td className="p-3 text-zinc-500 text-sm italic">{exampleRow.who}</td>
                <td className="p-3 text-zinc-500 text-sm italic">{exampleRow.ideas}</td>
              </tr>
            )}
            {rows.map((row, index) => (
              <tr key={index} className="border-t border-zinc-800/50">
                <td className="p-2">
                  <Input 
                    value={row.strength || ''}
                    onChange={(e) => updateRow(index, 'strength', e.target.value)}
                    className="bg-transparent border-none text-white text-sm"
                    placeholder="Strength"
                  />
                </td>
                <td className="p-2">
                  <Input 
                    value={row.proof || ''}
                    onChange={(e) => updateRow(index, 'proof', e.target.value)}
                    className="bg-transparent border-none text-white text-sm"
                    placeholder="Evidence"
                  />
                </td>
                <td className="p-2">
                  <Input 
                    value={row.who || ''}
                    onChange={(e) => updateRow(index, 'who', e.target.value)}
                    className="bg-transparent border-none text-white text-sm"
                    placeholder="Who needs this?"
                  />
                </td>
                <td className="p-2">
                  <Input 
                    value={row.ideas || ''}
                    onChange={(e) => updateRow(index, 'ideas', e.target.value)}
                    className="bg-transparent border-none text-white text-sm"
                    placeholder="Possible businesses"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// AI Response Component - Now uses styled renderer
const AIResponse = ({ content, isLoading, title, type }) => {
  if (isLoading) {
    return (
      <Card className="bg-[#1a1814]/80 border-[#d4af37]/30 p-6 my-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#d4af37]/20 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#d4af37] animate-pulse" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 text-zinc-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Analyzing your work...</span>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (!content) return null;

  return (
    <Card className="bg-[#1a1814]/80 border-[#d4af37]/30 p-6 my-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-8 h-8 rounded-full bg-[#d4af37]/20 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-[#d4af37]" />
        </div>
        {title && <h4 className="text-[#d4af37] font-medium text-lg">{title}</h4>}
      </div>
      <div className="pl-11">
        <AIResponseRenderer content={content} type={type} />
      </div>
    </Card>
  );
};

// Core Opportunity Card Component
const CoreOpportunityCard = ({ opportunity, isLoading, onGenerate }) => {
  if (isLoading) {
    return (
      <Card className="bg-gradient-to-br from-[#252015] to-[#1a1814] border-[#d4af37]/40 p-6 my-6">
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 text-[#d4af37] animate-spin" />
          <span className="text-[#d4af37]">Identifying your Core Opportunity...</span>
        </div>
      </Card>
    );
  }

  if (!opportunity) {
    return (
      <Card className="bg-[#1a1814]/80 border-[#d4af37]/30 p-6 my-6">
        <div className="text-center">
          <Target className="w-12 h-12 text-[#d4af37]/50 mx-auto mb-4" />
          <p className="text-zinc-400 mb-4">Your Core Opportunity hasn't been generated yet.</p>
          <Button onClick={onGenerate} className="btn-gold rounded-lg">
            <Sparkles className="w-4 h-4 mr-2" />
            Tap to generate your Core Opportunity
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-[#252015] to-[#1a1814] border-2 border-[#d4af37]/40 p-6 my-6 shadow-lg shadow-[#d4af37]/10">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[#d4af37] flex items-center justify-center">
          <Target className="w-5 h-5 text-black" />
        </div>
        <div>
          <span className="text-[#d4af37] text-xs font-bold tracking-widest">YOUR CORE OPPORTUNITY</span>
        </div>
      </div>
      
      {/* Winning Pain Point - Large Bold */}
      <h3 className="text-white text-2xl font-bold mb-4 leading-tight">
        {opportunity.winning_pain_point}
      </h3>
      
      {/* Why It Ranked First - 3 sentences */}
      <div className="bg-black/30 rounded-lg p-4 mb-4">
        <p className="text-zinc-300 leading-relaxed">
          {opportunity.why_it_ranked_first}
        </p>
      </div>
      
      {/* Unique Angle */}
      <div className="flex items-start gap-3 mb-4">
        <Zap className="w-5 h-5 text-[#d4af37] mt-0.5 flex-shrink-0" />
        <div>
          <span className="text-[#d4af37] text-xs font-bold tracking-wider block mb-1">YOUR UNIQUE ANGLE</span>
          <p className="text-zinc-300">{opportunity.unique_angle}</p>
        </div>
      </div>
      
      {/* Next Step Today */}
      <div className="border-t border-[#d4af37]/30 pt-4 mt-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#d4af37]/20 flex items-center justify-center flex-shrink-0">
            <ArrowRight className="w-4 h-4 text-[#d4af37]" />
          </div>
          <div>
            <span className="text-[#d4af37] text-xs font-bold tracking-wider block mb-1">YOUR NEXT STEP TODAY</span>
            <p className="text-white font-medium">{opportunity.next_step_today}</p>
          </div>
        </div>
      </div>
    </Card>
  );
};

// Idea Selection Component
const IdeaSelector = ({ ideas, selectedIdea, onSelect }) => {
  if (!ideas || ideas.length === 0) return null;
  
  // Parse ideas if string
  const parsedIdeas = typeof ideas === 'string' ? 
    ideas.split(/\d+\.\s+/).filter(Boolean).map(idea => ({ text: idea.trim() })) :
    ideas;

  return (
    <div className="space-y-4 my-6">
      <h3 className="text-white font-medium">Select Your ONE Idea</h3>
      <p className="text-zinc-400 text-sm">Choose the idea you want to focus on for the rest of this sprint.</p>
      
      <div className="space-y-3">
        {parsedIdeas.map((idea, index) => (
          <div
            key={index}
            onClick={() => onSelect(idea.text || idea)}
            className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
              selectedIdea === (idea.text || idea)
                ? 'border-[#d4af37] bg-[#d4af37]/10' 
                : 'border-zinc-700 hover:border-zinc-600'
            }`}
          >
            <p className="text-zinc-300">{idea.text || idea}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Conversation Tracker Component
const ConversationTracker = ({ data, onChange, fields, count }) => {
  const [conversations, setConversations] = useState(data || Array(count).fill({}));

  const updateConversation = (index, field, value) => {
    const newConvos = [...conversations];
    newConvos[index] = { ...newConvos[index], [field]: value };
    setConversations(newConvos);
    onChange(newConvos);
  };

  const completedCount = conversations.filter(c => c.name && c.has_problem).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-white font-medium">Validation Conversations</h3>
        <span className="text-zinc-400 text-sm">{completedCount}/{count} completed</span>
      </div>
      
      {conversations.map((convo, index) => (
        <Card key={index} className="bg-[#1a1814]/60 border-zinc-800 p-5">
          <h4 className="text-[#d4af37] text-sm font-medium mb-4">Conversation {index + 1}</h4>
          <div className="grid md:grid-cols-2 gap-4">
            {fields.map(field => (
              <div key={field.id} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                <label className="block text-zinc-400 text-xs mb-2">{field.label}</label>
                {field.type === 'select' ? (
                  <div className="flex gap-2 flex-wrap">
                    {field.options.map(opt => (
                      <Button
                        key={opt}
                        variant={convo[field.id] === opt ? "default" : "outline"}
                        size="sm"
                        onClick={() => updateConversation(index, field.id, opt)}
                        className={convo[field.id] === opt 
                          ? "bg-[#d4af37] text-black hover:bg-[#d4af37]/90" 
                          : "border-zinc-700 text-zinc-400 hover:border-[#d4af37]/50"}
                      >
                        {opt}
                      </Button>
                    ))}
                  </div>
                ) : field.type === 'textarea' ? (
                  <Textarea
                    value={convo[field.id] || ''}
                    onChange={(e) => updateConversation(index, field.id, e.target.value)}
                    className="bg-[#0f0d0a] border-zinc-700 text-white text-sm"
                    placeholder={field.placeholder}
                    rows={2}
                  />
                ) : (
                  <Input
                    value={convo[field.id] || ''}
                    onChange={(e) => updateConversation(index, field.id, e.target.value)}
                    className="bg-[#0f0d0a] border-zinc-700 text-white text-sm"
                    placeholder={field.placeholder}
                  />
                )}
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
};

// Decision Selector
const DecisionSelector = ({ options, value, onChange }) => (
  <div className="space-y-4">
    {options.map(option => (
      <div
        key={option.value}
        onClick={() => onChange(option.value)}
        className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
          value === option.value 
            ? 'border-[#d4af37] bg-[#d4af37]/10' 
            : 'border-zinc-700 hover:border-zinc-600'
        }`}
      >
        <div className="flex items-center gap-4">
          <span className="text-3xl">{option.emoji}</span>
          <div>
            <p className="text-white font-medium text-lg">{option.title}</p>
            <p className="text-zinc-400">{option.description}</p>
          </div>
        </div>
      </div>
    ))}
  </div>
);

// Social Proof Cards
const SocialProofCards = ({ stories }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!stories?.length) return null;

  return (
    <div className="my-8">
      <button 
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-[#d4af37] text-sm mb-4 hover:underline"
      >
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        {expanded ? 'Hide' : 'Show'} founder stories
      </button>
      
      {expanded && (
        <div className="grid md:grid-cols-3 gap-4">
          {stories.map((story, index) => (
            <Card key={index} className="bg-[#1a1814]/60 border-zinc-800 p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-[#d4af37]/20 flex items-center justify-center text-[#d4af37] text-sm font-medium">
                  {story.name[0]}
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{story.name}</p>
                  <p className="text-zinc-500 text-xs">{story.location}</p>
                </div>
              </div>
              <p className="text-zinc-400 text-sm leading-relaxed">"{story.quote}"</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Shareable Card Generator
const ShareableCard = ({ userName, businessName, onGenerate }) => {
  const [cardGenerated, setCardGenerated] = useState(false);
  const canvasRef = useRef(null);
  
  const generateCard = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = 1200;
    canvas.height = 630;
    
    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, 630);
    gradient.addColorStop(0, '#1a1612');
    gradient.addColorStop(1, '#0a0908');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 1200, 630);
    
    // Gold glow at top
    const glowGradient = ctx.createRadialGradient(600, 0, 0, 600, 0, 400);
    glowGradient.addColorStop(0, 'rgba(212, 175, 55, 0.15)');
    glowGradient.addColorStop(1, 'transparent');
    ctx.fillStyle = glowGradient;
    ctx.fillRect(0, 0, 1200, 400);
    
    // Main text
    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 48px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${userName} went from`, 600, 250);
    
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 64px Inter, sans-serif';
    ctx.fillText('Prayer to Prototype', 600, 330);
    
    ctx.fillStyle = '#d4af37';
    ctx.font = 'bold 48px Inter, sans-serif';
    ctx.fillText('in 7 days', 600, 400);
    
    // Business name
    if (businessName) {
      ctx.fillStyle = '#a1a1aa';
      ctx.font = '28px Inter, sans-serif';
      ctx.fillText(`Building: ${businessName}`, 600, 480);
    }
    
    // Logo text
    ctx.fillStyle = '#d4af37';
    ctx.font = '20px Inter, sans-serif';
    ctx.fillText('PRAYER TO PROTOTYPE', 600, 580);
    
    setCardGenerated(true);
    onGenerate && onGenerate();
  };
  
  const downloadCard = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const link = document.createElement('a');
    link.download = 'prayer-to-prototype-completion.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
    
    toast.success('Card downloaded!');
  };

  return (
    <div className="space-y-4">
      <canvas ref={canvasRef} className="hidden" />
      
      {!cardGenerated ? (
        <Button onClick={generateCard} className="btn-gold rounded-lg w-full py-6">
          <Sparkles className="w-5 h-5 mr-2" />
          Generate My Completion Card
        </Button>
      ) : (
        <div className="space-y-4">
          <Card className="bg-gradient-to-b from-[#1a1612] to-[#0a0908] border-[#d4af37]/30 p-8 text-center">
            <p className="text-[#d4af37] text-2xl font-medium mb-2">{userName} went from</p>
            <p className="text-white text-4xl font-bold mb-2">Prayer to Prototype</p>
            <p className="text-[#d4af37] text-2xl font-medium mb-4">in 7 days</p>
            {businessName && <p className="text-zinc-400">Building: {businessName}</p>}
            <p className="text-[#d4af37] text-sm mt-6">PRAYER TO PROTOTYPE</p>
          </Card>
          
          <Button onClick={downloadCard} className="btn-gold rounded-lg w-full">
            <Download className="w-4 h-4 mr-2" />
            Download Card
          </Button>
        </div>
      )}
    </div>
  );
};

// Day 6 Content Component - Landing Page Builder
const Day6Content = ({ formData, journeyData, handleFieldChange, API }) => {
  const [isGeneratingCopy, setIsGeneratingCopy] = useState(false);
  const [isGeneratingHTML, setIsGeneratingHTML] = useState(false);
  const [generatedCopy, setGeneratedCopy] = useState(formData.day6_copy || null);
  const [generatedHTML, setGeneratedHTML] = useState(formData.landing_page_html || '');
  const [viewMode, setViewMode] = useState('desktop'); // 'desktop' or 'mobile'
  const [showPreview, setShowPreview] = useState(!!formData.landing_page_html);
  const [copyError, setCopyError] = useState(null);
  const iframeRef = useRef(null);

  // Check if we have enough data from Days 1-4
  const hasPainMatrix = journeyData.pain_matrix?.some(p => p.pain?.trim());
  const hasStrengths = journeyData.strengths_list?.some(s => s.strength?.trim());
  const hasSelectedIdea = !!journeyData.selected_idea || !!formData.selected_idea;
  const hasAnyData = hasPainMatrix || hasStrengths || hasSelectedIdea;

  // Build context from Days 1-4 data
  const buildContext = () => {
    const painPoints = journeyData.pain_matrix?.filter(p => p.pain?.trim()).slice(0, 3) || [];
    const strengths = journeyData.strengths_list?.filter(s => s.strength?.trim()).slice(0, 3) || [];
    const selectedIdea = journeyData.selected_idea || formData.selected_idea || '';
    const mvpScope = journeyData.smallest_delivery || formData.smallest_delivery || '';
    const targetAudience = painPoints[0]?.who || 'your target customers';
    
    return {
      painPoints: painPoints.map(p => p.pain).join(', '),
      targetAudience,
      strengths: strengths.map(s => s.strength).join(', '),
      selectedIdea,
      mvpScope,
      headline: journeyData.headline || formData.headline || '',
      subheadline: journeyData.subheadline || formData.subheadline || '',
      founderStory: journeyData.founder_story || formData.founder_story || ''
    };
  };

  // Generate landing page copy
  const generateCopy = async () => {
    setIsGeneratingCopy(true);
    setCopyError(null);
    
    try {
      const context = buildContext();
      const prompt = `Generate landing page copy for this business:

BUSINESS IDEA: ${context.selectedIdea || 'A business solving real problems'}
TARGET AUDIENCE: ${context.targetAudience}
KEY PAIN POINTS: ${context.painPoints || 'Pain points identified by the founder'}
MVP SCOPE: ${context.mvpScope || 'A simple landing page with waitlist'}
FOUNDER STRENGTHS: ${context.strengths || 'Unique founder abilities'}

Generate the following sections as a JSON object:
{
  "headline": "A compelling headline that speaks to the pain (max 10 words)",
  "subheadline": "The transformation you offer (max 20 words)",
  "problem": "Describe the problem in 2-3 sentences",
  "solution": "Describe your solution in 2-3 sentences",
  "benefits": ["Benefit 1", "Benefit 2", "Benefit 3"],
  "cta": "Call to action text (e.g., Join the Waitlist)",
  "founderStory": "2-3 sentences about why the founder built this",
  "socialProof": "A placeholder for future testimonials"
}

Return ONLY the JSON object, no additional text.`;

      const response = await axios.post(`${API}/ai/generate`, {
        prompt,
        context
      }, { withCredentials: true });

      // Try to parse as JSON, fall back to raw text
      let copyData;
      try {
        const jsonMatch = response.data.content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          copyData = JSON.parse(jsonMatch[0]);
        } else {
          throw new Error('No JSON found');
        }
      } catch (e) {
        // Fallback: create structure from raw text
        copyData = {
          headline: context.selectedIdea || 'Your Solution Awaits',
          subheadline: 'Transform how you solve this problem',
          problem: 'Many people struggle with this challenge every day.',
          solution: 'We created a better way to address this need.',
          benefits: ['Save time', 'Reduce stress', 'Get results'],
          cta: 'Join the Waitlist',
          founderStory: 'Built by someone who experienced this problem firsthand.',
          socialProof: 'Join hundreds of others who have found success.'
        };
      }

      setGeneratedCopy(copyData);
      handleFieldChange('day6_copy', copyData);
      toast.success('Landing page copy generated!');
    } catch (error) {
      console.error('Copy generation error:', error);
      setCopyError('Failed to generate copy. Please try again.');
      toast.error('Failed to generate copy');
    } finally {
      setIsGeneratingCopy(false);
    }
  };

  // Generate full HTML landing page
  const generateLandingPage = async () => {
    if (!generatedCopy) {
      toast.error('Please generate copy first');
      return;
    }

    setIsGeneratingHTML(true);
    
    try {
      const prompt = `Create a complete, self-contained HTML landing page with this copy:

HEADLINE: ${generatedCopy.headline}
SUBHEADLINE: ${generatedCopy.subheadline}
PROBLEM: ${generatedCopy.problem}
SOLUTION: ${generatedCopy.solution}
BENEFITS: ${generatedCopy.benefits?.join(', ')}
CTA: ${generatedCopy.cta}
FOUNDER STORY: ${generatedCopy.founderStory}

Design requirements:
- Dark background using gradient from #1a1612 to #0a0908
- Gold accent color #d4af37
- White text on dark backgrounds
- Include Google Font: Inter
- Mobile-responsive using CSS flexbox/grid
- Include a hero section with headline and CTA
- Problem/solution section
- 3 benefits with icons (use simple Unicode symbols)
- Founder story section
- Email capture form with gold submit button
- Clean, modern aesthetic with subtle hover effects
- Add CSS transitions for smooth interactions

Return ONLY the complete HTML code starting with <!DOCTYPE html> and ending with </html>. No markdown, no explanations.`;

      const response = await axios.post(`${API}/ai/generate`, {
        prompt
      }, { withCredentials: true });

      // Extract HTML from response
      let html = response.data.content;
      
      // Clean up the response - remove markdown code blocks if present
      html = html.replace(/```html\n?/gi, '').replace(/```\n?/gi, '').trim();
      
      // Ensure it starts with DOCTYPE
      if (!html.toLowerCase().startsWith('<!doctype')) {
        const doctypeMatch = html.match(/<!DOCTYPE[\s\S]*?<\/html>/i);
        if (doctypeMatch) {
          html = doctypeMatch[0];
        }
      }

      setGeneratedHTML(html);
      handleFieldChange('landing_page_html', html);
      setShowPreview(true);
      toast.success('Landing page generated!');
    } catch (error) {
      console.error('HTML generation error:', error);
      toast.error('Failed to generate landing page');
    } finally {
      setIsGeneratingHTML(false);
    }
  };

  // Copy individual field to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  // Copy full HTML
  const copyFullHTML = () => {
    navigator.clipboard.writeText(generatedHTML);
    toast.success('Full HTML copied to clipboard!');
  };

  // Download HTML file
  const downloadHTML = () => {
    const blob = new Blob([generatedHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'landing-page.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('HTML file downloaded!');
  };

  // Render copy card
  const CopyCard = ({ label, content, fieldId }) => {
    if (!content) return null;
    
    const displayContent = Array.isArray(content) ? content.join('\n• ') : content;
    
    return (
      <Card className="bg-[#1a1814]/80 border-zinc-800 p-4 mb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p className="text-[#d4af37] text-xs uppercase tracking-wider mb-2">{label}</p>
            <p className="text-white whitespace-pre-line">
              {Array.isArray(content) ? `• ${displayContent}` : content}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => copyToClipboard(displayContent, label)}
            className="text-zinc-400 hover:text-[#d4af37] flex-shrink-0"
          >
            <Copy className="w-4 h-4" />
          </Button>
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Status indicator */}
      {!hasAnyData && (
        <Card className="bg-yellow-500/10 border-yellow-500/30 p-4">
          <p className="text-yellow-400 text-sm flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Complete Days 1-4 first to get personalized landing page copy, or generate with defaults.
          </p>
        </Card>
      )}

      {/* Generate Copy Button */}
      <div className="flex items-center gap-4">
        <Button 
          onClick={generateCopy}
          disabled={isGeneratingCopy}
          className="btn-gold rounded-lg"
        >
          {isGeneratingCopy ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Generating Copy...
            </>
          ) : (
            <>
              <Wand2 className="w-4 h-4 mr-2" />
              Generate Landing Page Copy
            </>
          )}
        </Button>
        {hasAnyData && (
          <span className="text-zinc-500 text-sm">
            ✓ Using your data from Days 1-4
          </span>
        )}
      </div>

      {copyError && (
        <p className="text-red-400 text-sm">{copyError}</p>
      )}

      {/* Generated Copy Cards */}
      {generatedCopy && (
        <div className="space-y-4">
          <h3 className="text-white font-medium flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#d4af37]" />
            Your Landing Page Copy
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <CopyCard label="Headline" content={generatedCopy.headline} />
            <CopyCard label="Subheadline" content={generatedCopy.subheadline} />
          </div>
          
          <CopyCard label="The Problem" content={generatedCopy.problem} />
          <CopyCard label="Your Solution" content={generatedCopy.solution} />
          <CopyCard label="Key Benefits" content={generatedCopy.benefits} />
          <CopyCard label="Call to Action" content={generatedCopy.cta} />
          <CopyCard label="Founder Story" content={generatedCopy.founderStory} />
          
          {/* Build Landing Page Button */}
          <div className="pt-4 border-t border-zinc-800">
            <Button 
              onClick={generateLandingPage}
              disabled={isGeneratingHTML}
              className="btn-gold rounded-lg w-full py-6"
            >
              {isGeneratingHTML ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Building Your Landing Page...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Build My Landing Page
                </>
              )}
            </Button>
            <p className="text-zinc-500 text-sm text-center mt-2">
              AI will create a complete, responsive HTML landing page
            </p>
          </div>
        </div>
      )}

      {/* Landing Page Preview */}
      {showPreview && generatedHTML && (
        <Card className="bg-[#1a1814]/60 border-zinc-800 p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-medium flex items-center gap-2">
              <Monitor className="w-5 h-5 text-[#d4af37]" />
              Live Preview
            </h3>
            
            <div className="flex items-center gap-2">
              {/* View Toggle */}
              <div className="flex bg-zinc-800 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('desktop')}
                  className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                    viewMode === 'desktop' 
                      ? 'bg-[#d4af37] text-black' 
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  <Monitor className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('mobile')}
                  className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                    viewMode === 'mobile' 
                      ? 'bg-[#d4af37] text-black' 
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                </button>
              </div>
              
              {/* Action Buttons */}
              <Button
                variant="outline"
                size="sm"
                onClick={copyFullHTML}
                className="border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37]/10"
              >
                <Copy className="w-4 h-4 mr-1" />
                Copy HTML
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadHTML}
                className="border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37]/10"
              >
                <Download className="w-4 h-4 mr-1" />
                Download
              </Button>
            </div>
          </div>
          
          {/* Iframe Preview */}
          <div 
            className={`bg-white rounded-lg overflow-hidden transition-all duration-300 mx-auto ${
              viewMode === 'mobile' ? 'max-w-[375px]' : 'w-full'
            }`}
            style={{ height: '600px' }}
          >
            <iframe
              ref={iframeRef}
              srcDoc={generatedHTML}
              title="Landing Page Preview"
              className="w-full h-full border-0"
              sandbox="allow-scripts"
            />
          </div>
          
          <p className="text-zinc-500 text-sm text-center mt-4">
            This is a live preview. Copy the HTML to use on your own domain or hosting service.
          </p>
        </Card>
      )}

      {/* Fallback: Show raw HTML in textarea if iframe doesn't render */}
      {showPreview && generatedHTML && (
        <details className="mt-4">
          <summary className="text-zinc-500 text-sm cursor-pointer hover:text-zinc-300">
            View raw HTML code
          </summary>
          <Card className="bg-[#0f0d0a] border-zinc-800 p-4 mt-2">
            <pre className="text-xs text-zinc-400 overflow-x-auto whitespace-pre-wrap max-h-64 overflow-y-auto">
              {generatedHTML}
            </pre>
          </Card>
        </details>
      )}
    </div>
  );
};

// Main DayPage Component
const DayPage = ({ user }) => {
  const { dayNumber } = useParams();
  const navigate = useNavigate();
  const day = parseInt(dayNumber);
  const content = dayContent[day];

  const [showPrayer, setShowPrayer] = useState(true);
  const [prayerComplete, setPrayerComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [journeyData, setJourneyData] = useState({});
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [coreOpportunity, setCoreOpportunity] = useState(null);
  const [coreOpportunityLoading, setCoreOpportunityLoading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [streak, setStreak] = useState(0);
  const [completedDays, setCompletedDays] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);
  const saveTimeout = useRef(null);

  useEffect(() => {
    fetchData();
  }, [day]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dayResponse, allDataResponse, progressResponse] = await Promise.all([
        axios.get(`${API}/journey/day/${day}`, { withCredentials: true }),
        axios.get(`${API}/journey/all-data`, { withCredentials: true }),
        axios.get(`${API}/journey/progress`, { withCredentials: true })
      ]);
      
      setFormData(dayResponse.data.form_data || {});
      setJourneyData(allDataResponse.data.data || {});
      setCompletedDays(progressResponse.data.completed_days || 0);
      
      // Calculate streak
      const progress = progressResponse.data.progress || [];
      let currentStreak = 0;
      for (let i = progress.length - 1; i >= 0; i--) {
        if (progress[i].completed) currentStreak++;
        else break;
      }
      setStreak(currentStreak);
      
      // Check if prayer was completed
      if (dayResponse.data.form_data?.prayer_completed) {
        setShowPrayer(false);
        setPrayerComplete(true);
      }
      
      // Load AI response if saved
      if (dayResponse.data.form_data?.ai_response) {
        setAiResponse(dayResponse.data.form_data.ai_response);
      }
      
      // Load Core Opportunity if saved
      if (dayResponse.data.form_data?.core_opportunity) {
        setCoreOpportunity(dayResponse.data.form_data.core_opportunity);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveField = useCallback(async (field, value) => {
    try {
      await axios.post(`${API}/journey/save-progress`, { day, field, value }, { withCredentials: true });
      // Show saved indicator
      setShowSavedIndicator(true);
      setTimeout(() => setShowSavedIndicator(false), 1500);
    } catch (error) {
      console.error('Auto-save error:', error);
    }
  }, [day]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => saveField(field, value), 1000);
  };

  const generateAI = async (promptType) => {
    setAiLoading(true);
    try {
      const prompt = buildPrompt(promptType);
      const response = await axios.post(`${API}/ai/generate`, {
        prompt,
        context: { ...journeyData, ...formData }
      }, { withCredentials: true });

      const aiContent = response.data.content;
      setAiResponse(aiContent);
      handleFieldChange('ai_response', aiContent);
      
      // Extract and save specific data based on prompt type
      if (promptType === 'pattern_analysis') {
        // Extract top pain points
        const painMatrix = formData.pain_matrix || [];
        const topPains = painMatrix.filter(r => r.pain?.trim()).slice(0, 3).map(r => r.pain);
        handleFieldChange('top_pain_points', topPains);
        
        // Automatically generate Core Opportunity as a second pass
        setTimeout(() => generateCoreOpportunity(), 500);
      }
      
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error("Failed to generate AI response");
    } finally {
      setAiLoading(false);
    }
  };

  // Generate Core Opportunity as a separate second pass
  const generateCoreOpportunity = async () => {
    setCoreOpportunityLoading(true);
    try {
      const allData = { ...journeyData, ...formData };
      const painMatrix = allData.pain_matrix || [];
      
      // Get the pain points for analysis
      const painList = painMatrix.filter(r => r.pain?.trim()).map((r, i) => 
        `${i+1}. Pain: ${r.pain} | Who: ${r.who} | Solutions: ${r.solutions} | Missing: ${r.missing}`
      ).join('\n');
      
      // Get strengths if available
      const strengths = allData.strengths_list || [];
      const strengthList = strengths.filter(s => s.strength?.trim()).map(s => 
        `- ${s.strength}: ${s.proof || 'No proof provided'}`
      ).join('\n');

      const prompt = `Based on this Pain Point Matrix, identify the #1 Core Opportunity for this founder:

PAIN POINTS:
${painList}

${strengthList ? `FOUNDER'S STRENGTHS (from Day 2 if available):
${strengthList}` : ''}

Respond in this EXACT JSON format only, no other text:
{
  "winning_pain_point": "The exact pain point title that ranks #1",
  "why_it_ranked_first": "First sentence explaining urgency. Second sentence explaining market gap. Third sentence explaining founder's unique position to solve it.",
  "unique_angle": "One sentence connecting this opportunity to the founder's background, skills, or lived experience that gives them an edge.",
  "next_step_today": "One specific, actionable step they can take in the next 2 hours to validate this opportunity."
}`;

      const response = await axios.post(`${API}/ai/generate`, {
        prompt,
        context: allData
      }, { withCredentials: true });

      // Parse the JSON response
      let coreOpp;
      try {
        const jsonMatch = response.data.content.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          coreOpp = JSON.parse(jsonMatch[0]);
        }
      } catch (e) {
        console.error('Failed to parse Core Opportunity JSON:', e);
      }

      // Validate the response has required fields
      if (coreOpp && coreOpp.winning_pain_point && coreOpp.why_it_ranked_first && coreOpp.next_step_today) {
        setCoreOpportunity(coreOpp);
        handleFieldChange('core_opportunity', coreOpp);
        toast.success('Core Opportunity identified!');
      } else {
        // Fallback: create from the first pain point
        const topPain = painMatrix.find(r => r.pain?.trim());
        if (topPain) {
          coreOpp = {
            winning_pain_point: topPain.pain,
            why_it_ranked_first: `This problem affects ${topPain.who || 'many people'} who currently rely on ${topPain.solutions || 'inadequate solutions'}. The gap in the market is clear: ${topPain.missing || 'a better solution is needed'}. You have the awareness and insight to address this need.`,
            unique_angle: "Your lived experience with this problem gives you authentic understanding that generic solutions lack.",
            next_step_today: "Talk to one person who faces this problem today and ask them: 'What have you tried to solve this, and why didn't it work?'"
          };
          setCoreOpportunity(coreOpp);
          handleFieldChange('core_opportunity', coreOpp);
        }
      }
      
    } catch (error) {
      console.error('Core Opportunity generation error:', error);
      toast.error("Failed to generate Core Opportunity");
    } finally {
      setCoreOpportunityLoading(false);
    }
  };

  const buildPrompt = (type) => {
    const allData = { ...journeyData, ...formData };
    
    switch (type) {
      case 'pattern_analysis':
        const painMatrix = allData.pain_matrix || [];
        const painList = painMatrix.filter(r => r.pain).map((r, i) => 
          `${i+1}. Pain: ${r.pain} | Who: ${r.who} | Solutions: ${r.solutions} | Missing: ${r.missing}`
        ).join('\n');
        
        return `Analyze this Pain Point Matrix and identify patterns:

${painList}

Provide:
1. TOP 3 MOST URGENT PROBLEMS - which pain points are most severe and underserved?
2. PATTERN ANALYSIS - what themes connect multiple pain points?
3. WHO KEEPS SHOWING UP - who is the recurring persona facing these problems?
4. OPPORTUNITY RANKING - which problems are worth solving based on urgency and gap in solutions?

Be specific and reference the actual pain points they listed.`;

      case 'unfair_advantage':
        const strengths = allData.strengths_list || [];
        const strengthList = strengths.filter(s => s.strength).map(s => 
          `- ${s.strength}: ${s.proof}`
        ).join('\n');
        
        const bridge = allData.strength_bridge || [];
        const bridgeList = bridge.filter(b => b.strength).map(b =>
          `- ${b.strength} → Benefits: ${b.who} → Ideas: ${b.ideas}`
        ).join('\n');
        
        const askThree = allData.ask_three_people || {};
        const externalFeedback = ['person1', 'person2', 'person3']
          .filter(p => askThree[p]?.response)
          .map(p => `- ${askThree[p].name || 'Someone'}: "${askThree[p].response}"`)
          .join('\n');
        
        // Get Day 1 pain points for context
        const day1PainPoints = allData.top_pain_points || [];
        const day1Matrix = allData.pain_matrix || [];
        const painPointsContext = day1PainPoints.length > 0 
          ? day1PainPoints.join('\n- ') 
          : day1Matrix.filter(p => p.pain).slice(0, 5).map(p => p.pain).join('\n- ');
        
        return `Based on this founder's strengths and business bridge:

STRENGTHS:
${strengthList}

STRENGTH-TO-BUSINESS BRIDGE:
${bridgeList}

${externalFeedback ? `WHAT OTHERS SAY ABOUT THEM:\n${externalFeedback}\n` : ''}
PAIN POINTS FROM DAY 1 (for context):
- ${painPointsContext || 'Not yet documented'}

Please provide THREE things:

1. UNFAIR ADVANTAGE STATEMENT (2-3 sentences in bold)
A compelling founder-market fit summary that explains why THIS founder is uniquely positioned to solve problems in their space. What combination of skills, experience, and story makes them credible? Make it personal and specific, not generic.

2. TOP 2 PAIN POINTS THAT BEST MATCH THESE STRENGTHS
Based on the Day 1 data, which problems align best with this founder's abilities? Reference specific pain points they listed.

3. FOUNDER-MARKET FIT SCORE: X/10
Give a score out of 10 with a one-sentence explanation of why. Be honest but encouraging.`;

      case 'generate_ideas':
        return `Generate 3-5 ranked business ideas based on this founder's work:

TOP PAIN POINTS FROM DAY 1:
${(allData.top_pain_points || []).join('\n')}

UNFAIR ADVANTAGE:
${allData.ai_response || 'Not yet generated'}

STRENGTHS:
${(allData.strengths_list || []).filter(s => s.strength).map(s => s.strength).join(', ')}

For each idea provide:
1. ONE-SENTENCE DESCRIPTION - what is it?
2. FOUNDER-MARKET FIT - why is this founder right for this?
3. MVP VERSION - what's the smallest test?

Rank them from strongest fit to weakest. Be creative but grounded in their actual strengths and the real problems they identified.`;

      case 'generate_copy':
        return `Generate landing page copy for this business:

SELECTED IDEA: ${allData.selected_idea || 'Not selected'}
MVP SCOPE: ${allData.smallest_delivery || 'Not defined'}
TARGET AUDIENCE: People facing ${allData.top_pain_points?.[0] || 'the problem identified'}
FOUNDER STORY: ${allData.ai_response || ''}

Generate:
1. HEADLINE - speaks directly to their pain (max 10 words)
2. SUBHEADLINE - the transformation you offer (max 20 words)
3. BENEFIT 1 - addresses a specific pain point
4. BENEFIT 2 - addresses a specific pain point
5. BENEFIT 3 - addresses a specific pain point
6. CALL TO ACTION - what should they do?
7. FOUNDER STORY - 2-3 sentences about why the founder built this
8. FAQ 1 - common objection and answer
9. FAQ 2 - common objection and answer
10. FAQ 3 - common objection and answer

Write in a warm, authentic tone. No corporate speak.`;

      case 'build_landing_page':
        return `Create a complete, mobile-responsive HTML landing page using this copy:

Headline: ${allData.headline || 'Your Headline'}
Subheadline: ${allData.subheadline || 'Your Subheadline'}
Benefit 1: ${allData.benefit_1 || 'Benefit 1'}
Benefit 2: ${allData.benefit_2 || 'Benefit 2'}
Benefit 3: ${allData.benefit_3 || 'Benefit 3'}
CTA: ${allData.cta || 'Join the Waitlist'}
Founder Story: ${allData.founder_story || 'Why I built this...'}

Design requirements:
- Dark background (#1a1612 to #0a0908 gradient)
- Gold accent color (#d4af37)
- White text
- Clean, modern aesthetic
- Email capture form
- Mobile responsive
- Include Font: Inter from Google Fonts

Return ONLY the complete HTML code.`;

      default:
        return '';
    }
  };

  const handlePrayerComplete = () => {
    setShowPrayer(false);
    setPrayerComplete(true);
    handleFieldChange('prayer_completed', true);
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
      // Save all current form data
      for (const [field, value] of Object.entries(formData)) {
        await saveField(field, value);
      }
      toast.success('Draft saved!');
    } catch (error) {
      console.error('Save draft error:', error);
      toast.error('Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCopy = () => {
    handleFieldChange('landing_copy_saved', true);
    toast.success('Copy saved! You can use this on Day 6.');
  };

  const handleCompleteDay = async () => {
    setCompleting(true);
    try {
      await axios.post(`${API}/journey/complete-day`, {
        day,
        data: formData
      }, { withCredentials: true });

      toast.success(`Day ${day} completed!`);
      
      // Day 1 completion: redirect to payment gate if user hasn't paid
      if (day === 1 && !user.has_paid) {
        navigate('/day1-complete');
        return;
      }
      
      if (day < 7) {
        navigate(`/day/${day + 1}`);
      } else {
        navigate('/completion');
      }
    } catch (error) {
      console.error('Complete error:', error);
      toast.error("Failed to complete day");
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1a1612] via-[#0f0d0a] to-[#0a0908] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#d4af37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!content) {
    navigate('/dashboard');
    return null;
  }

  // Show prayer overlay
  if (showPrayer && content.prayer && !prayerComplete) {
    return <PrayerOverlay prayer={content.prayer} scripture={content.scripture} onComplete={handlePrayerComplete} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1612] via-[#0f0d0a] to-[#0a0908] relative">
      {/* Dawn glow effect */}
      <div className="dawn-glow" />
      
      {/* Saved Indicator */}
      {showSavedIndicator && (
        <div 
          className="fixed top-4 right-4 z-[100] flex items-center gap-2 px-3 py-2 rounded-lg animate-fade-in"
          style={{
            background: 'rgba(10,9,8,0.9)',
            border: '1px solid rgba(212,175,55,0.3)'
          }}
        >
          <Check className="w-4 h-4 text-[#d4af37]" />
          <span className="text-[#d4af37] text-[0.75rem]">Saved</span>
        </div>
      )}
      
      {/* Header */}
      <header className="border-b border-zinc-800/50 bg-[#1a1612]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-zinc-400 hover:text-white">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Dashboard
          </Button>
          <div className="flex items-center gap-2 text-zinc-500 text-sm">
            <Clock className="w-4 h-4" />
            {content.time}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Progress Header with Gamification */}
        <ProgressHeader currentDay={day} completedDays={completedDays} streak={streak} />

        {/* Day Header */}
        <div className="mb-8">
          <p className="text-[#d4af37] text-sm uppercase tracking-[0.3em] mb-2">{content.phase}</p>
          <h1 className="text-3xl sm:text-4xl font-light text-white mb-4">{content.title}</h1>
          
          <Card className="bg-[#1a1814]/60 border-zinc-800 p-4 inline-block">
            <p className="text-zinc-300 italic">"{content.scripture.text}"</p>
            <p className="text-[#d4af37] text-sm mt-2">{content.scripture.reference}</p>
          </Card>
        </div>

        {/* Context Cards from previous days */}
        {content.contextCards && (
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {content.contextCards.map(type => (
              <ContextCards key={type} type={type} data={journeyData} />
            ))}
          </div>
        )}

        {/* Narrative Hook */}
        {content.narrativeHook && (
          <Card className="bg-[#1a1814]/60 border-zinc-800 p-6 mb-8">
            <p className="text-zinc-300 leading-relaxed whitespace-pre-line">{content.narrativeHook}</p>
          </Card>
        )}

        {/* Day 1: Pain Point Matrix */}
        {day === 1 && (
          <>
            {/* Example Cards */}
            <ExampleCards examples={content.examples} />
            
            <Card className="bg-[#1a1814]/60 border-zinc-800 p-6 mb-6">
              <PainPointMatrix
                data={formData.pain_matrix}
                onChange={(data) => handleFieldChange('pain_matrix', data)}
                matrixPrompt={content.matrixPrompt}
                onSaveDraft={handleSaveDraft}
                isSaving={isSaving}
              />
            </Card>

            <SocialProofCards stories={content.socialProof} />

            {/* AI Button with minimum requirement */}
            {(formData.pain_matrix?.filter(r => r.pain?.trim()).length >= (content.aiButton?.minRows || 3)) ? (
              <Button onClick={() => generateAI('pattern_analysis')} disabled={aiLoading} className="btn-gold rounded-lg mb-6">
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {content.aiButton.label}
              </Button>
            ) : (
              <Card className="bg-[#1a1814]/60 border-zinc-800 p-4 mb-6">
                <p className="text-zinc-400 text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-zinc-600" />
                  {content.aiButton?.lockedMessage || "Fill in at least 3 pain points to unlock your AI analysis."}
                </p>
              </Card>
            )}

            <AIResponse content={aiResponse} isLoading={aiLoading} title="Pattern Analysis" type="pattern_analysis" />
            
            {/* Core Opportunity Card - Generated as second pass */}
            {(aiResponse || coreOpportunity) && (
              <CoreOpportunityCard 
                opportunity={coreOpportunity} 
                isLoading={coreOpportunityLoading} 
                onGenerate={generateCoreOpportunity}
              />
            )}
          </>
        )}

        {/* Day 2: Strengths Audit */}
        {day === 2 && (
          <>
            {/* Strength Examples */}
            <StrengthExamples examples={content.strengthExamples} />
            
            <Card className="bg-[#1a1814]/60 border-zinc-800 p-6 mb-6">
              <StrengthsList
                data={formData.strengths_list}
                onChange={(data) => handleFieldChange('strengths_list', data)}
                strengthsPrompt={content.strengthsPrompt}
                onSaveDraft={handleSaveDraft}
                isSaving={isSaving}
              />
            </Card>

            <Card className="bg-[#1a1814]/60 border-zinc-800 p-6 mb-6">
              <StrengthBridgeTable
                data={formData.strength_bridge}
                onChange={(data) => handleFieldChange('strength_bridge', data)}
                exampleRow={content.bridgeExampleRow}
              />
            </Card>

            {/* Ask 3 People Exercise */}
            {content.askThreePeople && (
              <div className="mb-6">
                <AskThreePeople
                  config={content.askThreePeople}
                  data={formData.ask_three_people}
                  onChange={(data) => handleFieldChange('ask_three_people', data)}
                />
              </div>
            )}

            {/* AI Button with minimum requirement */}
            {(formData.strengths_list?.filter(s => s.strength?.trim()).length >= (content.aiButton?.minStrengths || 3)) ? (
              <Button onClick={() => generateAI('unfair_advantage')} disabled={aiLoading} className="btn-gold rounded-lg mb-6">
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {content.aiButton.label}
              </Button>
            ) : (
              <Card className="bg-[#1a1814]/60 border-zinc-800 p-4 mb-6">
                <p className="text-zinc-400 text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-zinc-600" />
                  {content.aiButton?.lockedMessage || "Add at least 3 strengths to unlock your Unfair Advantage analysis."}
                </p>
              </Card>
            )}

            <AIResponse content={aiResponse} isLoading={aiLoading} title="Your Unfair Advantage" type="unfair_advantage" />
          </>
        )}

        {/* Day 3: Idea Generation */}
        {day === 3 && (
          <>
            <Button onClick={() => generateAI('generate_ideas')} disabled={aiLoading} className="btn-gold rounded-lg mb-6">
              {aiLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Generate My Business Ideas
            </Button>

            <AIResponse content={aiResponse} isLoading={aiLoading} title="Your Business Ideas" type="ideas" />

            {aiResponse && (
              <Card className="bg-[#1a1814]/60 border-[#d4af37]/20 p-6 mt-6">
                <h3 className="text-white font-medium mb-4">Select Your ONE Idea</h3>
                <Textarea
                  value={formData.selected_idea || ''}
                  onChange={(e) => handleFieldChange('selected_idea', e.target.value)}
                  className="bg-[#0f0d0a] border-zinc-700 text-white"
                  placeholder="Type or paste the idea you want to focus on..."
                  rows={3}
                />
                <p className="text-zinc-500 text-sm mt-2">This will be your focus for the rest of the sprint.</p>
              </Card>
            )}
          </>
        )}

        {/* Day 4: MVP Scoping */}
        {day === 4 && (
          <>
            {/* Scope Comparison */}
            {content.scopeComparison && (
              <div className="grid md:grid-cols-3 gap-4 mb-8">
                <Card className="bg-red-500/5 border-red-500/20 p-4">
                  <p className="text-red-400 text-sm font-medium mb-2">🔴 {content.scopeComparison.tooBig.label}</p>
                  <p className="text-zinc-400 text-sm">{content.scopeComparison.tooBig.example}</p>
                </Card>
                <Card className="bg-yellow-500/5 border-yellow-500/20 p-4">
                  <p className="text-yellow-400 text-sm font-medium mb-2">🟡 {content.scopeComparison.stillTooBig.label}</p>
                  <p className="text-zinc-400 text-sm">{content.scopeComparison.stillTooBig.example}</p>
                </Card>
                <Card className="bg-green-500/5 border-green-500/20 p-4">
                  <p className="text-green-400 text-sm font-medium mb-2">🟢 {content.scopeComparison.rightSized.label}</p>
                  <p className="text-zinc-400 text-sm">{content.scopeComparison.rightSized.example}</p>
                </Card>
              </div>
            )}

            {content.exercises.map(ex => (
              <Card key={ex.id} className="bg-[#1a1814]/60 border-zinc-800 p-6 mb-4">
                <h3 className="text-white font-medium mb-2">{ex.title}</h3>
                {ex.question && <p className="text-zinc-400 text-sm mb-4">{ex.question}</p>}
                
                {ex.type === 'textarea' && (
                  <Textarea
                    value={formData[ex.id] || ''}
                    onChange={(e) => handleFieldChange(ex.id, e.target.value)}
                    className="bg-[#0f0d0a] border-zinc-700 text-white"
                    placeholder="Describe..."
                    rows={4}
                  />
                )}
                
                {ex.type === 'checklist' && (
                  <Textarea
                    value={formData[ex.id] || ''}
                    onChange={(e) => handleFieldChange(ex.id, e.target.value)}
                    className="bg-[#0f0d0a] border-zinc-700 text-white"
                    placeholder="1.\n2.\n3.\n4.\n5."
                    rows={6}
                  />
                )}
                
                {ex.phases && (
                  <div className="space-y-4">
                    {ex.phases.map(phase => (
                      <div key={phase.id}>
                        <label className="block text-zinc-400 text-sm mb-2">{phase.label}</label>
                        <Textarea
                          value={formData[phase.id] || ''}
                          onChange={(e) => handleFieldChange(phase.id, e.target.value)}
                          className="bg-[#0f0d0a] border-zinc-700 text-white"
                          placeholder={phase.placeholder}
                          rows={2}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </>
        )}

        {/* Day 5: Landing Page Copy */}
        {day === 5 && (
          <>
            <Button onClick={() => generateAI('generate_copy')} disabled={aiLoading} className="btn-gold rounded-lg mb-6">
              {aiLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Generate My Landing Page Copy
            </Button>

            <AIResponse content={aiResponse} isLoading={aiLoading} title="Generated Copy" type="copy" />

            <Card className="bg-[#1a1814]/60 border-zinc-800 p-6 mt-6">
              <h3 className="text-white font-medium mb-4">Edit Your Copy</h3>
              <p className="text-zinc-400 text-sm mb-6">Customize each section to match your voice.</p>
              
              <div className="space-y-4">
                {content.copyFields.map(field => (
                  <div key={field.id}>
                    <label className="block text-zinc-400 text-sm mb-2">{field.label}</label>
                    {field.type === 'textarea' ? (
                      <Textarea
                        value={formData[field.id] || ''}
                        onChange={(e) => handleFieldChange(field.id, e.target.value)}
                        className="bg-[#0f0d0a] border-zinc-700 text-white"
                        placeholder={field.placeholder}
                        rows={3}
                      />
                    ) : (
                      <Input
                        value={formData[field.id] || ''}
                        onChange={(e) => handleFieldChange(field.id, e.target.value)}
                        className="bg-[#0f0d0a] border-zinc-700 text-white"
                        placeholder={field.placeholder}
                      />
                    )}
                  </div>
                ))}
              </div>

              <Button onClick={handleSaveCopy} className="btn-gold rounded-lg mt-6">
                <Save className="w-4 h-4 mr-2" />
                Save My Copy
              </Button>
              
              {formData.landing_copy_saved && (
                <p className="text-[#d4af37] text-sm mt-3 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Copy saved! Ready for Day 6.
                </p>
              )}
            </Card>
          </>
        )}

        {/* Day 6: Build Landing Page */}
        {day === 6 && (
          <Day6Content 
            formData={formData}
            journeyData={journeyData}
            handleFieldChange={handleFieldChange}
            API={API}
          />
        )}

        {/* Day 7: Validation + Decision */}
        {day === 7 && (
          <>
            {/* Validation Script */}
            <Card className="bg-[#1a1814]/60 border-zinc-800 p-6 mb-6">
              <h3 className="text-white font-medium mb-4">Your Validation Script</h3>
              <div className="bg-[#0f0d0a] rounded-lg p-4">
                <p className="text-zinc-300 italic">
                  "Hey [NAME], I'm exploring an idea to help <span className="text-[#d4af37]">{journeyData.icp_name || 'people like you'}</span> who struggle with <span className="text-[#d4af37]">{journeyData.top_pain_points?.[0] || 'this problem'}</span>. Have you ever dealt with this? If yes, what have you tried so far? If something solved it cleanly, would you pay for it? What would feel worth it to you?"
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const script = `Hey [NAME], I'm exploring an idea to help ${journeyData.icp_name || 'people like you'} who struggle with ${journeyData.top_pain_points?.[0] || 'this problem'}. Have you ever dealt with this? If yes, what have you tried so far? If something solved it cleanly, would you pay for it? What would feel worth it to you?`;
                  navigator.clipboard.writeText(script);
                  toast.success("Script copied!");
                }}
                className="text-[#d4af37] mt-3"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Script
              </Button>
            </Card>

            {/* Conversation Tracker */}
            <ConversationTracker
              data={formData.conversations}
              onChange={(data) => handleFieldChange('conversations', data)}
              fields={content.conversationTracker.fields}
              count={content.conversationTracker.count}
            />

            {/* Decision Guide */}
            <Card className="bg-[#1a1814]/60 border-zinc-800 p-6 mt-6 mb-6">
              <h3 className="text-white font-medium mb-4">Decision Guide</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🟢</span>
                  <span className="text-zinc-300">{content.decisionGuide.go.range} → {content.decisionGuide.go.action}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🟡</span>
                  <span className="text-zinc-300">{content.decisionGuide.iterate.range} → {content.decisionGuide.iterate.action}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🔴</span>
                  <span className="text-zinc-300">{content.decisionGuide.pivot.range} → {content.decisionGuide.pivot.action}</span>
                </div>
              </div>
            </Card>

            {/* Decision Selector */}
            <Card className="bg-[#1a1814]/60 border-zinc-800 p-6 mb-6">
              <h3 className="text-white font-medium mb-6">Your Decision</h3>
              <DecisionSelector
                options={content.decision.options}
                value={formData.decision}
                onChange={(value) => handleFieldChange('decision', value)}
              />
            </Card>

            {/* Commitment Declaration */}
            {formData.decision && (
              <Card className="bg-[#1a1814]/60 border-[#d4af37]/20 p-6 mb-6">
                <h3 className="text-white font-medium mb-4">Commitment Declaration</h3>
                <Textarea
                  value={formData.commitment_statement || ''}
                  onChange={(e) => handleFieldChange('commitment_statement', e.target.value)}
                  className="bg-[#0f0d0a] border-zinc-700 text-white"
                  placeholder={content.commitment.template}
                  rows={4}
                />
              </Card>
            )}

            {/* Shareable Card */}
            {formData.decision && (
              <Card className="bg-[#1a1814]/60 border-[#d4af37]/20 p-6 mb-6">
                <h3 className="text-white font-medium mb-4">Your Completion Card</h3>
                <ShareableCard 
                  userName={user?.name?.split(' ')[0] || 'Founder'} 
                  businessName={journeyData.selected_idea || formData.selected_idea}
                />
              </Card>
            )}

            {/* Community Upsell */}
            <Card className="bg-[#d4af37]/10 border-[#d4af37]/30 p-8 text-center">
              <Users className="w-12 h-12 text-[#d4af37] mx-auto mb-4" />
              <h2 className="text-2xl font-light text-white mb-3">{content.communityUpsell.title}</h2>
              <p className="text-zinc-400 mb-6 max-w-md mx-auto">{content.communityUpsell.description}</p>
              <Button 
                className="btn-gold rounded-full px-8"
                onClick={async () => {
                  try {
                    await axios.post(`${API}/community/join`, {}, { withCredentials: true });
                    toast.success("You've joined the community waitlist!");
                  } catch (error) {
                    toast.error("Failed to join");
                  }
                }}
              >
                {content.communityUpsell.cta}
              </Button>
            </Card>

            {/* Final Blessing */}
            <Card className="bg-[#1a1814]/60 border-zinc-800 p-8 mt-6 text-center">
              <p className="text-zinc-300 italic leading-relaxed">{content.finalBlessing}</p>
            </Card>
          </>
        )}

        {/* Reflection */}
        {content.reflection && (
          <Card className="bg-[#1a1814]/60 border-zinc-800 p-6 mt-8">
            <h3 className="text-white font-medium mb-4">Evening Reflection</h3>
            <p className="text-zinc-400 italic mb-4">{content.reflection.prompt}</p>
            <Textarea
              value={formData.reflection || ''}
              onChange={(e) => handleFieldChange('reflection', e.target.value)}
              className="bg-[#0f0d0a] border-zinc-700 text-white"
              placeholder="Write your thoughts..."
              rows={4}
            />
            {content.reflection.prayerFocus && (
              <p className="text-zinc-500 text-sm mt-4 italic">
                Prayer focus: {content.reflection.prayerFocus}
              </p>
            )}
          </Card>
        )}

        {/* Complete Day Button */}
        <div className="flex items-center justify-between pt-8 mt-8 border-t border-zinc-800/50">
          {day > 1 && (
            <Button variant="ghost" onClick={() => navigate(`/day/${day - 1}`)} className="text-zinc-400 hover:text-white">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Day {day - 1}
            </Button>
          )}
          
          <Button 
            onClick={handleCompleteDay}
            disabled={completing}
            className="btn-gold rounded-full px-8 py-6 ml-auto gold-glow"
          >
            {completing ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : day === 7 ? (
              <span className="flex items-center gap-2">
                Complete Journey <CheckCircle2 className="w-5 h-5" />
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Day {day} Complete <ArrowRight className="w-5 h-5" />
              </span>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default DayPage;
